import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Receipt, Search, AlertTriangle, Clock, CheckCircle2, XCircle, FileText, Eye, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import ImportantNotesButton from '../components/ImportantNotesButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUT_PAIEMENT_BADGE = {
    impaye: { label: 'Impayée', cls: 'bg-red-100 text-red-700 border border-red-200' },
    partiellement_paye: { label: 'Partiellement payée', cls: 'bg-orange-100 text-orange-700 border border-orange-200' },
    totalement_paye: { label: 'Totalement payée', cls: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
};

const STATUT_ENVOI_BADGE = {
    non_envoye: { label: 'Non envoyée', cls: 'bg-gray-100 text-gray-600 border border-gray-200' },
    envoye: { label: 'Envoyée', cls: 'bg-blue-100 text-blue-700 border border-blue-200' },
};

function formatEUR(n) {
    const v = Number(n || 0);
    return v.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' EUR';
}
function formatDate(d) {
    if (!d) return '—';
    try {
        return new Date(d + 'T00:00').toLocaleDateString('fr-FR');
    } catch {
        return d;
    }
}

function StatCard({ icon: Icon, label, value, color, borderColor, active, onClick, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`bg-white rounded-xl border ${borderColor} shadow-sm p-4 flex items-center gap-3 transition-all text-left w-full ${active ? 'ring-2 ring-[#1B365D] -translate-y-0.5 shadow-md' : 'hover:scale-[1.01] hover:shadow-md'}`}
            data-testid={testId || `facture-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
        >
            <div className={`h-11 w-11 rounded-lg ${color} flex items-center justify-center shrink-0`}>
                <Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0">
                <div className="text-2xl font-bold text-[#1B365D] leading-none" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</div>
                <div className="text-xs text-gray-500 mt-1 truncate">{label}</div>
            </div>
        </button>
    );
}

export default function Facturation() {
    const navigate = useNavigate();
    const [stats, setStats] = useState({ total: 0, impaye: 0, partiellement_paye: 0, totalement_paye: 0, urgent: 0 });
    const [factures, setFactures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [activeFilter, setActiveFilter] = useState('all'); // all | impaye | partiellement_paye | totalement_paye | urgent

    const toggleFilter = (key) => {
        setActiveFilter(prev => (prev === key ? 'all' : key));
    };

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [sRes, fRes] = await Promise.all([
                axios.get(`${API_URL}/api/factures/stats`, { withCredentials: true }),
                axios.get(`${API_URL}/api/factures`, { withCredentials: true }),
            ]);
            setStats(sRes.data || {});
            setFactures(fRes.data || []);
        } catch (err) {
            toast.error('Erreur chargement factures');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
    }, []);

    const filtered = useMemo(() => {
        const s = search.trim().toLowerCase();
        // 1) filter by active status card
        let list = factures;
        if (activeFilter !== 'all') {
            if (activeFilter === 'urgent') {
                const today = new Date(); today.setHours(0, 0, 0, 0);
                const in5days = new Date(today.getTime() + 5 * 86400000);
                list = factures.filter(f => {
                    if (f.statut_paiement === 'totalement_paye') return false;
                    if (!f.date_demenagement) return false;
                    const d = new Date(f.date_demenagement + 'T00:00');
                    return d >= today && d <= in5days;
                });
            } else {
                list = factures.filter(f => f.statut_paiement === activeFilter);
            }
        }
        // 2) text search
        if (!s) return list;
        return list.filter(f => {
            return (
                (f.client_nom || '').toLowerCase().includes(s) ||
                (f.numero || '').toLowerCase().includes(s) ||
                (f.devis_reference || '').toLowerCase().includes(s) ||
                (f.client_telephone || '').toLowerCase().includes(s) ||
                (f.client_email || '').toLowerCase().includes(s)
            );
        });
    }, [factures, search, activeFilter]);

    const FILTER_LABEL = {
        all: 'Toutes les factures',
        impaye: 'Factures impayées',
        partiellement_paye: 'Factures partiellement payées',
        totalement_paye: 'Factures totalement payées',
        urgent: 'Factures urgentes (< 5 jours du chargement, non soldées)',
    };

    return (
        <Layout>
            <div className="space-y-5" data-testid="facturation-page">
                {/* Header */}
                <div>
                    <h1 className="text-2xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>Facturation</h1>
                    <p className="text-sm text-gray-500 mt-0.5">Gestion des factures clients</p>
                </div>

                {/* Stat cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    <StatCard icon={Receipt} label="Total des factures" value={stats.total}
                        color="bg-slate-100 text-slate-600" borderColor="border-gray-200"
                        active={activeFilter === 'all'}
                        onClick={() => setActiveFilter('all')}
                        testId="facture-stat-total" />
                    <StatCard icon={XCircle} label="Impayées" value={stats.impaye}
                        color="bg-red-100 text-red-600" borderColor="border-red-200/60"
                        active={activeFilter === 'impaye'}
                        onClick={() => toggleFilter('impaye')}
                        testId="facture-stat-impaye" />
                    <StatCard icon={Clock} label="Partiellement payées" value={stats.partiellement_paye}
                        color="bg-orange-100 text-orange-600" borderColor="border-orange-200/60"
                        active={activeFilter === 'partiellement_paye'}
                        onClick={() => toggleFilter('partiellement_paye')}
                        testId="facture-stat-partiellement" />
                    <StatCard icon={CheckCircle2} label="Totalement payées" value={stats.totalement_paye}
                        color="bg-emerald-100 text-emerald-600" borderColor="border-emerald-200/60"
                        active={activeFilter === 'totalement_paye'}
                        onClick={() => toggleFilter('totalement_paye')}
                        testId="facture-stat-paye" />
                    <StatCard icon={AlertTriangle} label="Urgent" value={stats.urgent || 0}
                        color="bg-violet-100 text-violet-600" borderColor="border-violet-200/60"
                        active={activeFilter === 'urgent'}
                        onClick={() => toggleFilter('urgent')}
                        testId="facture-stat-urgent" />
                </div>

                {/* Search */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <Input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Rechercher (nom, numéro de facture, numéro de devis, téléphone, email)…"
                            className="pl-9 bg-gray-50 border-gray-200"
                            data-testid="facture-search-input"
                        />
                    </div>
                </div>

                {/* Invoice list */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-[#1B365D]" />
                            <h2 className="font-semibold text-[#1B365D] text-sm">
                                {FILTER_LABEL[activeFilter]} <span className="text-gray-400 font-normal">({filtered.length})</span>
                            </h2>
                        </div>
                        {activeFilter !== 'all' && (
                            <button
                                onClick={() => setActiveFilter('all')}
                                className="text-xs px-2 py-1 rounded-md border border-gray-200 hover:bg-gray-50 text-gray-600"
                                data-testid="facture-clear-filter"
                            >
                                ✕ Voir toutes les factures
                            </button>
                        )}
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center py-16">
                            <Loader2 className="w-6 h-6 animate-spin text-[#1B365D]" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="py-16 text-center text-gray-400 text-sm">
                            <Receipt className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                            Aucune facture pour le moment
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50/70 text-[11px] uppercase text-gray-500 tracking-wide">
                                    <tr>
                                        <th className="text-left px-4 py-3 font-semibold">Client</th>
                                        <th className="text-left px-4 py-3 font-semibold">Date déménagement</th>
                                        <th className="text-left px-4 py-3 font-semibold">Facture N°</th>
                                        <th className="text-left px-4 py-3 font-semibold">Statut envoi</th>
                                        <th className="text-right px-4 py-3 font-semibold">Prix TTC</th>
                                        <th className="text-right px-4 py-3 font-semibold">Solde dû</th>
                                        <th className="text-left px-4 py-3 font-semibold">Statut</th>
                                        <th className="text-right px-4 py-3 font-semibold">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {filtered.map(f => {
                                        const paiement = STATUT_PAIEMENT_BADGE[f.statut_paiement] || STATUT_PAIEMENT_BADGE.impaye;
                                        const envoi = STATUT_ENVOI_BADGE[f.statut_envoi] || STATUT_ENVOI_BADGE.non_envoye;
                                        return (
                                            <tr
                                                key={f.id}
                                                onClick={() => navigate(`/facturation/${f.id}`)}
                                                className="hover:bg-gray-50/80 cursor-pointer transition-colors"
                                                data-testid={`facture-row-${f.id}`}
                                            >
                                                <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                                                    <span className="inline-flex items-center gap-2">
                                                        {f.facture_type === 'sous_traitance' && (
                                                            <span
                                                                className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-orange-700 text-white"
                                                                title={`Sous-traitance${f.sous_traitant_nom ? ' - ' + f.sous_traitant_nom : ''}`}
                                                                data-testid={`facture-st-badge-${f.id}`}
                                                            >
                                                                ST
                                                            </span>
                                                        )}
                                                        {f.client_nom || '—'}
                                                        <ImportantNotesButton
                                                            prospectId={f.prospect_id}
                                                            prospectName={f.client_nom || ''}
                                                            variant="icon-only"
                                                        />
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(f.date_demenagement)}</td>
                                                <td className="px-4 py-3 font-mono text-[#1B365D] whitespace-nowrap">{f.numero}</td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <Badge className={`text-[10px] ${envoi.cls}`}>{envoi.label}</Badge>
                                                </td>
                                                <td className="px-4 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">{formatEUR(f.prix_ttc)}</td>
                                                <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">
                                                    <span className={f.solde > 0 ? 'text-red-600' : 'text-emerald-600'}>{formatEUR(f.solde)}</span>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <Badge className={`text-[10px] ${paiement.cls}`}>{paiement.label}</Badge>
                                                </td>
                                                <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                    <button
                                                        onClick={() => navigate(`/facturation/${f.id}`)}
                                                        className="p-1.5 rounded-md hover:bg-[#1B365D]/5 text-[#1B365D] transition-colors"
                                                        data-testid={`facture-view-${f.id}`}
                                                        title="Voir la facture"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}
